/**
 * Nosto-HeaderSearch.js
 *
 * Autocomplete Nosto independiente para el buscador del header.
 * Usa exclusivamente el ProductCard universal.
 */
(function initializeNostoHeaderSearch(global) {
    "use strict";

    const SELECTORS = {
        root: "[data-nosto-header-search]",
        form: "[data-nosto-header-search-form]",
        input: "[data-nosto-header-search-input]",
        empty: "[data-nosto-header-search-empty]",
        active: "[data-nosto-header-search-active]",
        keywords: "[data-nosto-header-search-keywords]",
        results: "[data-nosto-header-search-results]",
        footer: "[data-nosto-header-search-footer]",
        footerLink: "[data-nosto-header-search-all-results]",
        total: "[data-nosto-header-search-total]",
        query: "[data-nosto-header-search-query]",
        productsCount: "[data-nosto-header-search-products-count]",
        productsTotal: "[data-nosto-header-search-products-total]",
    };

    const CARD_SELECTOR = "[data-product-card]";
    const RECENT_SEARCH_EVENT = "nosto:header-search:submitted";

    class NostoHeaderSearch {
        constructor(root) {
            this.root = root;
            this.form = root.querySelector(SELECTORS.form);
            this.input = root.querySelector(SELECTORS.input);
            this.emptyState = root.querySelector(SELECTORS.empty);
            this.activeState = root.querySelector(SELECTORS.active);
            this.keywordContainer = root.querySelector(SELECTORS.keywords);
            this.resultsContainer = root.querySelector(SELECTORS.results);
            this.footer = root.querySelector(SELECTORS.footer);
            this.footerLink = root.querySelector(SELECTORS.footerLink);
            this.totalElement = root.querySelector(SELECTORS.total);
            this.queryElement = root.querySelector(SELECTORS.query);
            this.productsCount = root.querySelector(SELECTORS.productsCount);
            this.productsTotal = root.querySelector(SELECTORS.productsTotal);

            this.modalSize = this.getPositiveNumber(root.dataset.nostoModalSize, 4);
            this.keywordSize = this.getPositiveNumber(root.dataset.nostoKeywordSize, 8);
            this.minimumQueryLength = this.getPositiveNumber(root.dataset.nostoMinimumQueryLength, 2);
            this.debounceDelay = this.getPositiveNumber(root.dataset.nostoDebounceDelay, 300);
            this.searchUrl = root.dataset.nostoSearchUrl || "/search";

            this.debounceTimer = null;
            this.latestRequestId = 0;
            this.client = null;
            this.hitStore = null;
            this.renderer = null;
            this.productViewService = null;
            this.quickBuyModal = null;

            this.boundHandleInput = this.handleInput.bind(this);
            this.boundHandleSubmit = this.handleSubmit.bind(this);
            this.boundHandleReset = this.handleReset.bind(this);
        }

        getPositiveNumber(value, fallback) {
            const number = Number(value);
            return Number.isFinite(number) && number > 0 ? number : fallback;
        }

        normalizeString(value) {
            return String(value ?? "").trim();
        }

        normalizeQuery(value) {
            return String(value || "").trim().replace(/\s+/g, " ");
        }

        normalizeProductId(value) {
            const normalized = this.normalizeString(value);
            if (!normalized) return "";
            return normalized.split("/").filter(Boolean).at(-1);
        }

        getHandle(hit) {
            const directHandle = this.normalizeString(hit?.handle || hit?.productHandle);
            if (directHandle) return directHandle;

            const url = this.normalizeString(hit?.url || hit?.productUrl);
            if (!url) return "";

            try {
                const parsed = new URL(url, global.location.origin);
                const parts = parsed.pathname.split("/").filter(Boolean);
                const index = parts.indexOf("products");
                return index >= 0 ? parts[index + 1] || "" : "";
            } catch (error) {
                return "";
            }
        }

        validateDependencies() {
            const required = [
                "NostoClient",
                "NostoHitStore",
                "NostoProductResultsRenderer",
                "NostoProductViewService",
            ];

            const missing = required.filter(
                (dependency) => typeof global[dependency] !== "function"
            );

            if (missing.length) {
                throw new Error(`Header Search requiere: ${missing.join(", ")}`);
            }
        }

        init() {
            this.validateDependencies();

            if (!this.form || !this.input || !this.resultsContainer) {
                throw new Error("Header Search no encontró su form, input o results container.");
            }

            this.createServices();
            this.bindEvents();
            this.showEmptyState();
            this.root.dataset.nostoHeaderSearchInitialized = "true";
            return this;
        }

        createServices() {
            this.client = new global.NostoClient({ debug: false });
            this.hitStore = new global.NostoHitStore({ debug: false });
            this.productViewService = this.getSharedProductViewService();
            this.initializeQuickBuy();

            this.renderer = new global.NostoProductResultsRenderer({
                container: this.resultsContainer,
                placeholderTagName: "li",
                statusTagName: "li",
                placeholderClass: "nosto-product-placeholder",
                productItemClass: "search-nosto-results__product",
                emptyMessage: "No encontramos productos.",
                errorMessage: "No fue posible cargar los productos.",
                productCardFactory: (options) => this.renderProductCard(options),
            });
        }

        getSharedProductViewService() {
            const shared = global.NOSTO_SHARED_PRODUCT_VIEW_SERVICE;

            if (shared && typeof shared.getView === "function") {
                return shared;
            }

            const service = new global.NostoProductViewService({
                cardView: "product-card",
                quickBuyView: "nosto-quick-buy",
                concurrency: 4,
                cache: true,
                debug: false,
            });

            global.NOSTO_SHARED_PRODUCT_VIEW_SERVICE = service;
            return service;
        }

        async renderProductCard({ hit, productId, container, context = {} }) {
            const normalizedProductId = this.normalizeProductId(productId);
            const handle = this.getHandle(hit);

            if (!handle) {
                throw new Error(
                    `No fue posible obtener el handle para el producto ${normalizedProductId || "desconocido"}.`
                );
            }

            const html = await this.productViewService.getView({
                handle,
                productId: normalizedProductId,
                view: "product-card",
                validationSelector: CARD_SELECTOR,
                context: {
                    ...context,
                    source: "nosto-search",
                    track: "autocomplete",
                },
            });

            const template = document.createElement("template");
            template.innerHTML = String(html).trim();

            const sourceCard = template.content.querySelector(CARD_SELECTOR);

            if (!sourceCard) {
                throw new Error("La vista product-card no contiene [data-product-card].");
            }

            const card = sourceCard.cloneNode(true);

            card.dataset.productSource = "nosto-search";
            card.dataset.nostoTrack = "autocomplete";
            card.dataset.nostoOriginalProductId = normalizedProductId;
            card.dataset.nostoQuery = this.normalizeString(context.query);

            container.replaceChildren(card);
            global.initializeProductCards?.(container);

            const handleCardClick = (event) => {
                const link = event.target.closest("[data-product-card-link]");

                if (!link || !card.contains(link)) {
                    return;
                }

                void this.recordProductClick(normalizedProductId, hit);
            };

            card.addEventListener("click", handleCardClick);

            return {
                root: card,
                destroy() {
                    card.removeEventListener("click", handleCardClick);
                    card.__productCardInstance?.destroy?.();
                },
            };
        }

        initializeQuickBuy() {
            if (typeof global.NostoQuickBuyModal !== "function") return;

            if (global.NOSTO_QUICK_BUY_MODAL) {
                this.quickBuyModal = global.NOSTO_QUICK_BUY_MODAL;
                return;
            }

            this.quickBuyModal = new global.NostoQuickBuyModal({
                viewService: this.productViewService,
                debug: false,
            });

            this.quickBuyModal.init();
            global.NOSTO_QUICK_BUY_MODAL = this.quickBuyModal;
        }

        bindEvents() {
            this.input.addEventListener("input", this.boundHandleInput);
            this.form.addEventListener("submit", this.boundHandleSubmit);
            this.form.addEventListener("reset", this.boundHandleReset);

            this.footerLink?.addEventListener("click", (event) => {
                event.preventDefault();
                const query = this.normalizeQuery(this.input.value);
                if (!query) return;
                this.goToSearch(query);
            });
        }

        handleInput() {
            global.clearTimeout(this.debounceTimer);
            const query = this.normalizeQuery(this.input.value);

            if (query.length < this.minimumQueryLength) {
                this.clearResults();
                this.showEmptyState();
                return;
            }

            this.showResultsState();
            this.debounceTimer = global.setTimeout(() => this.search(query), this.debounceDelay);
        }

        handleReset() {
            global.clearTimeout(this.debounceTimer);
            this.latestRequestId += 1;

            global.setTimeout(() => {
                this.clearResults();
                this.showEmptyState();
            }, 0);
        }

        handleSubmit(event) {
            event.preventDefault();
            const query = this.normalizeQuery(this.input.value);
            if (!query) return;
            this.goToSearch(query);
        }

        buildRequest(query) {
            return {
                query,
                products: {
                    fields: ["name", "url", "imageUrl"],
                    from: 0,
                    size: this.modalSize,
                },
                keywords: {
                    size: this.keywordSize,
                    fields: ["keyword", "_highlight.keyword", "_redirect"],
                },
            };
        }

        async search(query) {
            const requestId = ++this.latestRequestId;
            this.resultsContainer.setAttribute("aria-busy", "true");

            try {
                const response = await this.client.search(
                    this.buildRequest(query),
                    { redirect: false, track: "autocomplete" }
                );

                if (requestId !== this.latestRequestId) return;

                const products = Array.isArray(response?.products?.hits)
                    ? response.products.hits
                    : [];

                const keywords = Array.isArray(response?.keywords?.hits)
                    ? response.keywords.hits
                    : [];

                this.hitStore.replace(products);

                await this.renderer.render(products, {
                    mode: "replace",
                    context: {
                        source: "nosto-search",
                        track: "autocomplete",
                        query,
                    },
                });

                if (requestId !== this.latestRequestId) return;

                this.renderKeywords(keywords, query);
                this.renderFooter({
                    query: response?.query || query,
                    total: Number(response?.products?.total || 0),
                });

                this.showResultsState();
                global.refreshProductCards?.(this.resultsContainer);
            } catch (error) {
                if (requestId !== this.latestRequestId) return;

                console.error("[NostoHeaderSearch]", error);
                this.renderer.renderError(error);
                this.keywordContainer?.replaceChildren();

                if (this.footer) this.footer.hidden = true;
                this.showResultsState();
            } finally {
                if (requestId === this.latestRequestId) {
                    this.resultsContainer.setAttribute("aria-busy", "false");
                }
            }
        }

        renderKeywords(keywordHits, currentQuery) {
            if (!this.keywordContainer) return;

            this.keywordContainer.replaceChildren();
            const normalizedQuery = currentQuery.toLowerCase();

            const keywords = [
                ...new Set(
                    keywordHits
                        .map((hit) => typeof hit === "string" ? hit : hit?.keyword)
                        .map((keyword) => this.normalizeQuery(keyword))
                        .filter(Boolean)
                        .filter((keyword) => keyword.toLowerCase() !== normalizedQuery)
                ),
            ];

            const fragment = document.createDocumentFragment();

            keywords.forEach((keyword) => {
                const button = document.createElement("button");
                button.type = "button";
                button.className = "search-nosto-results__keyword";
                button.textContent = keyword;
                button.addEventListener("click", () => this.goToSearch(keyword));
                fragment.appendChild(button);
            });

            this.keywordContainer.appendChild(fragment);
        }

        renderFooter({ query, total }) {
            if (!this.footer || !this.footerLink) return;

            if (!query || total <= 0) {
                this.footer.hidden = true;
                return;
            }

            if (this.totalElement) this.totalElement.textContent = String(total);
            if (this.queryElement) this.queryElement.textContent = query;
            if (this.productsTotal) this.productsTotal.textContent = String(total);
            if (this.productsCount) this.productsCount.hidden = total <= 0;

            this.footerLink.href = `${this.searchUrl}?q=${encodeURIComponent(query)}`;
            this.footer.hidden = false;
        }

        async recordProductClick(productId, fallbackHit) {
            const hit = this.hitStore.get(productId) || fallbackHit;
            if (!hit) return false;

            try {
                return await this.client.recordSearchClick("autocomplete", hit);
            } catch (error) {
                console.warn("[NostoHeaderSearch] No fue posible registrar el click.", error);
                return false;
            }
        }

        registerRecentSearch(query) {
            this.root.dispatchEvent(
                new CustomEvent(RECENT_SEARCH_EVENT, {
                    bubbles: true,
                    detail: { query },
                })
            );
        }

        async goToSearch(query) {
            const normalizedQuery = this.normalizeQuery(query);
            if (!normalizedQuery) return;

            this.registerRecentSearch(normalizedQuery);

            try {
                await this.client.recordSearchSubmit(normalizedQuery);
            } catch (error) {
                console.warn("[NostoHeaderSearch] No fue posible registrar el submit.", error);
            }

            global.location.href = `${this.searchUrl}?q=${encodeURIComponent(normalizedQuery)}`;
        }

        clearResults() {
            this.latestRequestId += 1;
            this.hitStore?.clear();
            this.renderer?.clear();
            this.keywordContainer?.replaceChildren();
            if (this.footer) this.footer.hidden = true;
        }

        showEmptyState() {
            if (this.emptyState) this.emptyState.hidden = false;
            if (this.activeState) this.activeState.hidden = true;
            this.input?.setAttribute("aria-expanded", "false");
        }

        showResultsState() {
            if (this.emptyState) this.emptyState.hidden = true;
            if (this.activeState) this.activeState.hidden = false;
            this.input?.setAttribute("aria-expanded", "true");
        }

        destroy() {
            global.clearTimeout(this.debounceTimer);
            this.input?.removeEventListener("input", this.boundHandleInput);
            this.form?.removeEventListener("submit", this.boundHandleSubmit);
            this.form?.removeEventListener("reset", this.boundHandleReset);
            this.renderer?.clear();
        }
    }

    function initRoot(root) {
        if (root.dataset.nostoHeaderSearchInitialized === "true") return;

        try {
            const instance = new NostoHeaderSearch(root);
            instance.init();
            root.__nostoHeaderSearchInstance = instance;
        } catch (error) {
            console.error("[NostoHeaderSearch] No fue posible inicializar.", error);
        }
    }

    function init() {
        document.querySelectorAll(SELECTORS.root).forEach(initRoot);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, { once: true });
    } else {
        init();
    }

    document.addEventListener("shopify:section:load", init);
    global.NostoHeaderSearch = NostoHeaderSearch;
})(window);